import React, { useEffect, useRef, useState } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { createBranchScreens, getBranchScreens, removeBranchScreen } from '../../api/branchDisplayApi';

const keyFor = (id) => String(id ?? '');
const numberFor = (screen, index) => String(screen.screenNumber ?? index + 1).padStart(2, '0');

export default function BranchScreenManager({ branchId, screenId, canManage, currentProductCount, mutationBlocked = false, onScreensChange, onCurrentRemoved, compact = false }) {
  const [screens, setScreens] = useState([]);
  const [allowed, setAllowed] = useState(false);
  const [count, setCount] = useState(1);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);
  const addButton = useRef(null);
  const restoreFocus = useRef(false);
  const callbacks = useRef({ onScreensChange, onCurrentRemoved });
  callbacks.current = { onScreensChange, onCurrentRemoved };
  const validCount = Number.isInteger(Number(count)) && Number(count) >= 1 && Number(count) <= 20;
  const closeAdd = () => { restoreFocus.current = true; setAdding(false); };
  useEffect(() => {
    if (restoreFocus.current && !adding && !removing && !busy) {
      restoreFocus.current = false; addButton.current?.focus();
    }
  }, [adding, removing, busy]);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    getBranchScreens(branchId, { signal: controller.signal }).then((data) => {
      if (controller.signal.aborted) return;
      setScreens(data.screens);
      setAllowed(typeof data.canManage === 'boolean' ? data.canManage : canManage);
      setError('');
      callbacks.current.onScreensChange?.(data.screens);
    }).catch(() => { if (!controller.signal.aborted) setError('No se pudieron cargar las pantallas.'); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [branchId, attempt, canManage]);

  const create = async (event) => {
    event.preventDefault();
    if (!allowed || !validCount || busy) return;
    setBusy(true); setError('');
    try {
      const result = await createBranchScreens(branchId, Number(count));
      if (!alive.current) return;
      const next = [...screens, ...result.screens];
      setScreens(next); setCount(1); closeAdd();
      callbacks.current.onScreensChange?.(next);
      // With no current configuration left, open the first new screen.
      if (!screens.length) callbacks.current.onCurrentRemoved?.(result.screens[0]);
    } catch (failure) {
      if (alive.current) setError(failure?.response?.data?.message || 'No se pudieron crear las pantallas. Revisa la lista antes de reintentar.');
    } finally { if (alive.current) setBusy(false); }
  };
  const remove = async () => {
    if (!allowed || !removing || busy || (mutationBlocked && keyFor(removing.id) === keyFor(screenId))) return;
    setBusy(true); setError('');
    try {
      await removeBranchScreen(branchId, removing.id);
      if (!alive.current) return;
      const next = screens.filter((screen) => keyFor(screen.id) !== keyFor(removing.id));
      setScreens(next); restoreFocus.current = true; setRemoving(null);
      callbacks.current.onScreensChange?.(next);
      if (keyFor(removing.id) === keyFor(screenId)) callbacks.current.onCurrentRemoved?.(next[0] || null);
    } catch (failure) {
      if (alive.current) setError(failure?.response?.data?.message || 'No se pudo quitar la pantalla. Intenta nuevamente.');
    } finally { if (alive.current) setBusy(false); }
  };
  const currentIndex = screens.findIndex((screen) => keyFor(screen.id) === keyFor(screenId));
  const currentScreen = screens[currentIndex];

  return <section aria-label="Pantallas de esta sucursal" className="display-screen-manager">
    {compact ? <div className="display-screen-compact">
      <label><span className="sr-only">Pantalla</span><select aria-label="Pantalla" value={keyFor(screenId)} disabled={loading || busy || mutationBlocked || !screens.length} onChange={(event) => {
        if (mutationBlocked || busy || keyFor(screenId) === event.target.value) return;
        const next = screens.find((screen) => keyFor(screen.id) === event.target.value);
        if (next) window.location.assign(`/branches/${branchId}/highlights${next.id ? `/${next.id}` : ''}`);
      }}>
        {!screens.length && <option value="">{loading ? 'Cargando pantallas…' : 'Sin pantallas'}</option>}
        {screens.map((screen, index) => <option key={keyFor(screen.id)} value={keyFor(screen.id)}>Pantalla {numberFor(screen, index)} · {keyFor(screen.id) === keyFor(screenId) && currentProductCount !== undefined ? currentProductCount : screen.productCount || 0} productos</option>)}
      </select></label>
      {allowed && <>
        <button ref={addButton} type="button" className="display-screen-add" aria-label="Añadir pantallas" aria-expanded={adding} disabled={busy} onClick={() => { setAdding((value) => !value); setRemoving(null); setError(''); }}><FiPlus /></button>
        {currentScreen && <button type="button" className="display-quiet-button display-screen-remove" aria-label={`Quitar pantalla ${numberFor(currentScreen, currentIndex)}`} disabled={busy || mutationBlocked} onClick={() => { setRemoving({ ...currentScreen, number: numberFor(currentScreen, currentIndex) }); setAdding(false); setError(''); }}><FiX /></button>}
      </>}
    </div> : <div className="display-screen-strip">
      <span className="display-screen-label">Pantallas</span>
      <nav aria-label="Elegir pantalla" className="display-screen-tabs">
        {screens.map((screen, index) => {
          const selected = keyFor(screen.id) === keyFor(screenId);
          const number = numberFor(screen, index);
          const products = selected && currentProductCount !== undefined ? currentProductCount : screen.productCount || 0;
          return <div key={keyFor(screen.id)} className={`display-screen-tab ${selected ? 'is-selected' : ''}`}>
            <a aria-current={selected ? 'page' : undefined} aria-disabled={mutationBlocked || undefined} onClick={(event) => { if (mutationBlocked) event.preventDefault(); }} aria-label={`Pantalla ${number}, ${products} productos`} href={`/branches/${branchId}/highlights${screen.id ? `/${screen.id}` : ''}`}>
              <strong>{number}</strong><span>{products} {products === 1 ? 'producto' : 'productos'}</span>
            </a>
            {allowed && <button type="button" aria-label={`Quitar pantalla ${number}`} title={selected && mutationBlocked ? 'Espera a que se guarden los cambios' : `Quitar pantalla ${number}`} disabled={busy || (selected && mutationBlocked)} onClick={() => { setRemoving({ ...screen, number }); setAdding(false); setError(''); }}><FiX /></button>}
          </div>;
        })}
        {loading && <span className="display-screen-hint">Cargando…</span>}
        {!loading && !screens.length && <span className="display-screen-hint">Sin pantallas</span>}
        {allowed && <button ref={addButton} type="button" className="display-screen-add" aria-label="Añadir pantallas" aria-expanded={adding} disabled={busy} onClick={() => { setAdding((value) => !value); setRemoving(null); setError(''); }}><FiPlus /></button>}
      </nav>
    </div>}
    {adding && <form className="display-screen-popover" role="dialog" aria-label="Añadir pantallas" onSubmit={create} onKeyDown={(event) => { if (event.key === 'Escape' && !busy) closeAdd(); }}>
      <label htmlFor="display-screen-count">¿Cuántas pantallas quieres añadir?</label>
      <div><input id="display-screen-count" aria-label="Cantidad de pantallas" type="number" min="1" max="20" step="1" autoFocus value={count} onChange={(event) => setCount(event.target.value)} disabled={busy} />
        <button type="submit" className="display-primary-button" disabled={!validCount || busy}>{busy ? 'Creando…' : `Crear ${count || ''} ${Number(count) === 1 ? 'pantalla' : 'pantallas'}`}</button>
        <button type="button" className="display-quiet-button" onClick={closeAdd} disabled={busy}>Cancelar</button></div>
      <p>Se numeran automáticamente. Puedes añadir hasta 20 a la vez.</p>
    </form>}
    {removing && <div className="display-screen-popover" role="alertdialog" aria-label={`Quitar pantalla ${removing.number}`}>
      <strong>¿Quitar Pantalla {removing.number}?</strong>
      <p>Su enlace público dejará de mostrar productos. Las demás pantallas conservarán sus números y productos.</p>
      <div><button type="button" className="display-danger-button" onClick={remove} disabled={busy || (mutationBlocked && keyFor(removing.id) === keyFor(screenId))}>{busy ? 'Quitando…' : 'Quitar pantalla'}</button>
        <button type="button" className="display-quiet-button" onClick={() => { restoreFocus.current = true; setRemoving(null); }} disabled={busy}>Cancelar</button></div>
    </div>}
    {error && <p role="alert" className="display-screen-error">{error}<button type="button" disabled={busy} onClick={() => setAttempt((value) => value + 1)}>Actualizar lista</button></p>}
  </section>;
}
